import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, jsonBody, setCsrfToken } from '../api/client'
import type { CurrentUser, LoginResponse } from '../api/contracts'

type Credentials = { institution_code: string; username: string; password: string }

interface AuthContextValue {
  user: CurrentUser | null
  loading: boolean
  login(credentials: Credentials): Promise<void>
  logout(): Promise<void>
  refresh(): Promise<void>
  can(permission: string): boolean
  canAny(...permissions: string[]): boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const current = await api<CurrentUser>('/api/v1/auth/me')
      const csrf = await api<{ csrf_token: string }>('/api/v1/auth/csrf')
      setCsrfToken(csrf.csrf_token)
      setUser(current)
    } catch {
      setCsrfToken(null)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => void refresh(), [refresh])

  const login = useCallback(async (credentials: Credentials) => {
    const result = await api<LoginResponse>('/api/v1/auth/login', {
      method: 'POST',
      ...jsonBody(credentials),
    })
    setCsrfToken(result.csrf_token)
    setUser(result.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api('/api/v1/auth/logout', { method: 'POST' })
    } finally {
      setCsrfToken(null)
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login,
    logout,
    refresh,
    can: (permission) => Boolean(user?.permissions.includes(permission)),
    canAny: (...permissions) => permissions.some((permission) => user?.permissions.includes(permission)),
  }), [loading, login, logout, refresh, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
